import { getTopicListing } from 'common/__content-api/v4/get-topic-listing'
import { mapListPublication } from 'common/__api-mapping/v4/list-publication'
import { getCuration } from 'common/__content-api/v4/get-curation'
import { flatMap } from 'common/helpers/utils'
import {
    CurationData,
    ListingData,
    GeolocationCurationData,
    PromotionalData,
    ComponentServices,
    AggregatedData,
} from 'common/routing/page-definition'
import { getCurationFromRegion } from 'common/data/location-detection/helpers'
import { LocationState } from './location-detection/reducer'
import { ListArticleV4DTO } from 'publication-types'
import { scaleImage } from 'common/helpers/scaleImage'
import { DataLoaderGlobalParams } from './resources'
import { DataDefinition } from 'json-react-layouts-data-loader'
import { useSelector } from 'react-redux'
import { AppState } from 'common/store'
import { getCurationMetadata } from 'common/__content-api/v4/get-curation-metadata'
import {
    CardItem,
    MarketingRedirectTileCard,
    PublicationCardItem,
} from 'common/client-data-types/card-types'

export type ContentDataTypes =
    | AggregatedData
    | CurationData
    | ListingData
    | GeolocationCurationData
    | PromotionalData

export type ContentData = ContentDataTypes | ContentDataTypes[]

export type CollectionDataLoaded =
    | {
          loadMorePossible: boolean
          kind: 'listing'
          publications: CardItem[]
      }
    | {
          loadMorePossible: false
          kind: 'geolocation'
          location: LocationState
          publications: CardItem[]
      }
    | {
          loadMorePossible: false
          kind: 'curation'
          metadata: { [key: string]: any }
          publications: CardItem[]
      }
    | {
          loadMorePossible: false
          /** When multiple other collection sources are merged */
          kind: 'aggregated'
          publications: CardItem[]
          sourceCollections: CollectionDataLoaded[]
      }
    | {
          loadMorePossible: false
          kind: 'promotional'
          publications: CardItem[]
      }

export type ContentDataDefinition = DataDefinition<
    ContentDataTypes,
    CollectionDataLoaded,
    ComponentServices
>

export const loadCollectionData = async (
    services: DataLoaderGlobalParams,
    data: CurationData | ListingData | GeolocationCurationData | PromotionalData
): Promise<CollectionDataLoaded> => {
    if (data.type === 'listing') {
        // If fetching both gallery and article than we don't set a "kind" value so we get all article types back,
        // then after fetching, we filter the results to get the correct values
        const publicationKind =
            data.publicationKind === 'galleryAndArticle' ? undefined : data.publicationKind

        const topicListing = await getTopicListing(services, {
            /** We need to include both parentTopics to get all the children,
             * and topics to get the top level
             */
            parentTopics: data.topics.filter(t => t.indexOf('/') === -1),
            topics: data.topics,
            excludeTopics: data.excludeTopics,
            paging: data.paging,
            isSponsored: data.isSponsored,
            publicationKind,
            source: data.source,
            authors: data.authors,
            profiles: !data.hideProfiles,
        })

        let mappedListing = topicListing.documents
            .filter(listPublication => {
                if (data.publicationKind === 'galleryAndArticle') {
                    return ['gallery', 'article'].indexOf(listPublication.kind) >= 0
                }
                return true
            })
            .map(listPublication => mapListPublication(listPublication))

        // Orders the results requested via reverse chron paging, in chronological order
        if (data.sortResultsInChornologicalOrder === true) {
            mappedListing = mappedListing.sort((a, b) => {
                if (a.publicationDate && b.publicationDate) {
                    return (
                        new Date(a.publicationDate).getTime() -
                        new Date(b.publicationDate).getTime()
                    )
                }
                return 0
            })
        }

        return {
            loadMorePossible: topicListing.morePublicationsAvailable,
            kind: 'listing',
            publications: mappedListing,
        }
    }

    if (data.type === 'curation') {
        const curation = await getCuration(services, data.name, {
            profiles: !data.hideProfiles,
            offset: data.offset,
            pageSize: data.pageSize,
        })

        const mappedCollectionItems: CardItem[] = curation.articles.map(listPublication =>
            mapListPublication(listPublication)
        )

        if (data.injectMetadataStory && curation.metadata) {
            const metadataStory = curation.metadata[data.injectMetadataStory.metadataKey]

            if (metadataStory && isArticle(metadataStory)) {
                if (
                    data.injectMetadataStory.kind === 'marketing-redirect-tile' &&
                    metadataStory.kind === 'redirect' &&
                    metadataStory.mainImages.length &&
                    metadataStory.redirectUrl
                ) {
                    const imageData = metadataStory.mainImages[0]
                    const scaledSrc = scaleImage(imageData.src, 500, false)

                    const promoTile: MarketingRedirectTileCard = {
                        cardType: 'marketing-redirect-tile',
                        image: scaledSrc,
                        url: metadataStory.redirectUrl,
                        altText: imageData.captionText || '',
                    }

                    // Inject the promo tile in
                    mappedCollectionItems.splice(data.injectMetadataStory.position, 0, promoTile)
                    // Then trim the collection
                    mappedCollectionItems.splice(data.pageSize)
                }
            }
        }

        // Just inject the promo tile, dont trim the collection
        if (
            data.injectMetadataStory &&
            data.injectMetadataStory.kind === 'the-west-live-promo-tile'
        ) {
            mappedCollectionItems.splice(data.injectMetadataStory.position, 0, {
                cardType: 'the-west-live-promo-tile',
            })
        }

        return {
            loadMorePossible: false,
            kind: 'curation',
            metadata: curation.metadata,
            publications: mappedCollectionItems,
        }
    }

    if (data.type === 'geolocation') {
        const location = services.store.getState().geoLocation
        const name = getCurationFromRegion(location)
        const curation = await getCuration(services, name, {
            offset: data.offset,
            pageSize: data.pageSize,
        })

        const mappedCollectionItems = curation.articles.map(listPublication =>
            mapListPublication(listPublication)
        )

        return {
            loadMorePossible: false,
            kind: 'geolocation',
            location,
            publications: mappedCollectionItems,
        }
    }

    if (data.type === 'promotional') {
        let publications: PublicationCardItem[] = []
        try {
            const publicationDTO = await getCurationMetadata(services, {
                curationId: 'promotional-cards',
                metadataKey: data.metadataKey,
            })

            publications = [mapListPublication(publicationDTO)]
        } catch (err) {
            // Catching 404 errors. Don't need to display something went wrong in this case
        }

        return {
            loadMorePossible: false,
            kind: 'promotional',
            publications,
        }
    }

    // Need the as any here, because contentAreaComponent.data has been narrowed to never
    // But the switch statement cannot compile if we don't have this.
    // ugh
    throw new Error(`Unknown content area type of ${(data as any).type}`)
}

export const loadMultipleCollectionData = async (
    services: DataLoaderGlobalParams,
    data: Array<CurationData | ListingData | GeolocationCurationData | PromotionalData>
): Promise<CollectionDataLoaded> => {
    const loadedData = await Promise.all(data.map(datum => loadCollectionData(services, datum)))

    // multiple collection loading wont have any morePublicationsAvailable
    return {
        loadMorePossible: false,
        kind: 'aggregated',
        publications: flatMap(loadedData, v => v.publications),
        sourceCollections: loadedData,
    }
}

/**
 * Applies to cards, collections anything that requires the ContentDataDefinition data loading
 */
export const ContentDataDefinitionLoader: ContentDataDefinition = {
    useRuntimeParams: props => {
        // Check to see if any data definitions are of type geolocation
        const geoLocationPresent = Array.isArray(props)
            ? props.filter(def => def.type === 'geolocation').length > 0
            : props.type === 'geolocation'

        if (geoLocationPresent) {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            const region = useSelector((state: AppState) => state.geoLocation.userSelectedRegion)
            return { region }
        }
        // invalidate the whole content area if geoLocationPresent = true with a new cacheKey
        return {}
    },
    loadData: (props, services) =>
        props.type === 'aggregated'
            ? loadMultipleCollectionData(services, props.sources)
            : loadCollectionData(services, props),
}

function isArticle(metadata: any): metadata is ListArticleV4DTO {
    return metadata && (metadata.kind === 'article' || metadata.kind === 'redirect')
}
